import {
  ArrowLeft,
  User,
  MapPin,
  Flag,
  Info,
  Loader2,
  Building,
  UserCheck,
  Receipt,
} from 'lucide-react'
import { useState, useMemo, useCallback } from 'react'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { PlacesAutocompleteInput } from '@/components/ui/places-autocomplete'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useCreateBeneficiary } from '@/hooks/useCpn'
import { useGooglePlaces } from '@/hooks/useGooglePlaces'
import { TransferData } from '@/types/payment'
import {
  CPN_PAYMENT_REASONS,
  PAYMENT_REASON_LABELS,
  type CpnPaymentReason,
} from '@/utils/cpn'
import { logger } from '@/utils/logger'
import {
  validateField,
  validateBeneficiaryForm,
  getCountryConfig,
  formatPostalCode,
  sanitizeInput,
  type BeneficiaryFormData,
} from '@/utils/validation'

interface AddBeneficiaryScreenProps {
  transferData?: TransferData
  onNext: (beneficiaryId: string) => void
  onBack: () => void
  userId: string
}

interface FormErrors {
  firstName?: string
  lastName?: string
  street?: string
  city?: string
  stateProvince?: string
  postalCode?: string
  country?: string
  paymentReason?: string
}

const AddBeneficiaryScreen = ({
  transferData,
  onNext,
  onBack,
  userId,
}: AddBeneficiaryScreenProps) => {
  const [formData, setFormData] = useState<BeneficiaryFormData>({
    firstName: '',
    lastName: '',
    street: '',
    city: '',
    stateProvince: '',
    postalCode: '',
    country: transferData?.receiveCountry || '',
    isBusiness: false,
    isSelf: false,
    paymentReason: 'PMT022', // Default to "Remittance payments to friends/family"
  })

  const [errors, setErrors] = useState<FormErrors>({})
  const [touched, setTouched] = useState<Record<string, boolean>>({})
  const [showRecipientTypeDialog, setShowRecipientTypeDialog] = useState(true)

  const createBeneficiaryMutation = useCreateBeneficiary()

  // Get country configuration for dynamic labels and validation
  const countryConfig = useMemo(() => {
    return getCountryConfig(formData.country)
  }, [formData.country])

  // Google Places integration
  const { isLoaded, loadError, parseAddressComponents, getSearchOptions } =
    useGooglePlaces()

  const [isParsingAddress, setIsParsingAddress] = useState(false)

  // Memoize search options to prevent infinite re-renders in PlacesAutocompleteInput
  const searchOptions = useMemo(() => {
    return getSearchOptions(formData.country)
  }, [formData.country, getSearchOptions])

  const handleInputChange = (
    name: keyof BeneficiaryFormData,
    value: string
  ) => {
    let processedValue = value

    // Sanitize input based on field type
    if (
      name === 'firstName' ||
      name === 'lastName' ||
      name === 'city' ||
      name === 'stateProvince'
    ) {
      processedValue = sanitizeInput(value, 'name')
    } else if (name === 'street') {
      processedValue = sanitizeInput(value, 'address')
    } else if (name === 'country') {
      processedValue = value
        .toUpperCase()
        .replace(/[^A-Z]/g, '')
        .slice(0, 2)
    } else if (name === 'postalCode' && formData.country) {
      // Auto-format postal code based on country
      processedValue = formatPostalCode(value, formData.country)
    }

    setFormData((prev) => ({ ...prev, [name]: processedValue }))

    // Validate on change if field has been touched
    if (touched[name]) {
      const error = validateField(name, processedValue, formData.country)
      setErrors((prev) => ({ ...prev, [name]: error }))
    }
  }

  // Handle address selection from Places Autocomplete
  const handleAddressSelect = useCallback(
    async (placeId: string) => {
      setIsParsingAddress(true)
      try {
        const parsed = await parseAddressComponents(placeId, formData.country)
        if (parsed) {
          // Update all address fields
          setFormData((prev) => ({
            ...prev,
            street: parsed.streetAddress,
            city: parsed.city,
            stateProvince: parsed.stateProvince,
            postalCode: formatPostalCode(
              parsed.postalCode,
              parsed.country || formData.country
            ),
            country: parsed.country || formData.country,
          }))

          // Mark fields as touched since they were auto-filled
          setTouched({
            firstName: touched.firstName,
            lastName: touched.lastName,
            street: true,
            city: true,
            stateProvince: true,
            postalCode: true,
            country: true,
          })

          // Clear any previous errors for auto-filled fields
          setErrors((prev) => ({
            ...prev,
            street: undefined,
            city: undefined,
            stateProvince: undefined,
            postalCode: undefined,
            country: undefined,
          }))
        }
      } catch (error) {
        logger.error('Failed to parse address', error)
      } finally {
        setIsParsingAddress(false)
      }
    },
    [
      parseAddressComponents,
      formData.country,
      touched.firstName,
      touched.lastName,
    ]
  )

  const handleInputBlur = (name: keyof BeneficiaryFormData) => {
    setTouched((prev) => ({ ...prev, [name]: true }))
    const error = validateField(name, formData[name], formData.country)
    setErrors((prev) => ({ ...prev, [name]: error }))
  }

  const validateForm = (): boolean => {
    const validation = validateBeneficiaryForm(formData)

    setErrors(validation.errors)
    setTouched(
      Object.keys(formData).reduce((acc, key) => ({ ...acc, [key]: true }), {})
    )

    // Check if form is valid (no errors and required fields are filled)
    const hasNoErrors = Object.keys(validation.errors).length === 0
    const requiredFieldsFilled =
      formData.firstName.trim() !== '' &&
      formData.lastName.trim() !== '' &&
      formData.paymentReason.trim() !== '' &&
      formData.street.trim() !== '' &&
      formData.city.trim() !== '' &&
      formData.postalCode.trim() !== '' &&
      formData.country.trim() !== ''

    return hasNoErrors && requiredFieldsFilled
  }

  const handleSubmit = () => {
    if (!validateForm()) {
      return
    }

    createBeneficiaryMutation.mutate(
      {
        userId,
        data: formData,
      },
      {
        onSuccess: (response) => {
          onNext(response.beneficiaryUuid)
        },
        onError: (error) => {
          logger.error('Failed to create beneficiary', error)
        },
      }
    )
  }

  const isFormValid =
    Object.values(errors).every((error) => !error) &&
    Object.entries(formData).every(([key, value]) => {
      if (key === 'isBusiness' || key === 'isSelf') return true
      return typeof value === 'string' && value.trim() !== ''
    })

  // Handle recipient type selection
  const handleRecipientTypeSelect = (type: 'self' | 'other' | 'business') => {
    setFormData((prev) => ({
      ...prev,
      isBusiness: type === 'business',
      isSelf: type === 'self',
      // Lock payment reason to 'Transfer to own account' when sending to self
      paymentReason: type === 'self' ? 'PMT006' : prev.paymentReason,
    }))
    setShowRecipientTypeDialog(false)
  }

  return (
    <div className="flex flex-col h-[600px] dark:bg-gray-900 relative overflow-hidden">
      <div className="p-4 space-y-4 flex-1 overflow-y-auto dark:bg-gray-900">
        {/* Header */}
        <div className="flex items-center space-x-3">
          <Button variant="ghost" size="sm" onClick={onBack} className="p-1">
            <ArrowLeft className="w-5 h-5 dark:text-gray-300" />
          </Button>
          <div>
            <h1 className="text-lg font-bold text-gray-900 dark:text-white">
              Add Recipient
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {transferData ? `Enter recipient details` : `Add a new recipient`}
            </p>
          </div>
        </div>

        {/* Transfer Summary - Only show when transferData is provided */}
        {transferData && (
          <Card className="p-3 bg-blue-50 border-blue-200 dark:bg-blue-900 dark:border-blue-700">
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-600 dark:text-gray-300">
                Sending to {transferData.receiveCountry}
              </span>
              <span className="font-medium dark:text-white">
                {transferData.sendAmount} {transferData.sendCurrency} →{' '}
                {transferData.receiveAmount} {transferData.receiveCurrency}
              </span>
            </div>
            {countryConfig && (
              <div className="mt-2 pt-2 border-t border-blue-200 dark:border-blue-600">
                <p className="text-xs text-blue-700 dark:text-blue-300 flex items-center">
                  <Flag className="w-3 h-3 mr-1" />
                  Creating Recipient for {countryConfig.name} (
                  {countryConfig.code})
                </p>
              </div>
            )}
          </Card>
        )}

        {/* Payment Reason */}
        <Card className="p-4 dark:bg-gray-800 dark:border-gray-700">
          <div className="flex items-center space-x-2 mb-3">
            <Receipt className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            <h3 className="font-medium text-gray-900 dark:text-white">
              Payment Reason
            </h3>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Reason for Payment *
            </label>
            <Select
              value={formData.paymentReason}
              onValueChange={(value) => {
                handleInputChange('paymentReason', value)
                setTouched((prev) => ({ ...prev, paymentReason: true }))
              }}
              disabled={formData.isSelf}
            >
              <SelectTrigger
                className={`w-full dark:bg-gray-900 dark:border-gray-600 dark:text-white ${
                  formData.isSelf
                    ? 'opacity-60 cursor-not-allowed'
                    : errors.paymentReason
                      ? 'border-red-500 dark:border-red-400'
                      : touched.paymentReason && !errors.paymentReason
                        ? 'border-green-500 dark:border-green-400'
                        : ''
                }`}
              >
                <SelectValue placeholder="Select payment reason" />
              </SelectTrigger>
              <SelectContent className="dark:bg-gray-900 dark:border-gray-600 max-w-[375px]">
                {CPN_PAYMENT_REASONS.map((code) => (
                  <SelectItem
                    key={code}
                    value={code}
                    className="dark:text-white dark:focus:bg-gray-700 dark:focus:text-white"
                  >
                    {PAYMENT_REASON_LABELS[code as CpnPaymentReason]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.paymentReason && (
              <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                {errors.paymentReason}
              </p>
            )}
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 flex items-center">
              <Info className="w-3 h-3 mr-1" />
              {formData.isSelf
                ? 'Payment reason is set to "Transfer to own account" for self transfers'
                : 'Select the purpose of this payment'}
            </p>
          </div>
        </Card>

        {/* Personal Information */}
        <Card className="p-4 dark:bg-gray-800 dark:border-gray-700">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              {formData.isBusiness ? (
                <Building className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              ) : (
                <User className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              )}
              <h3 className="font-medium text-gray-900 dark:text-white">
                {formData.isBusiness
                  ? 'Business Information'
                  : formData.isSelf
                    ? 'Your Information'
                    : 'Recipient Information'}
              </h3>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowRecipientTypeDialog(true)}
              className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
            >
              Change Type
            </Button>
          </div>

          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  First Name *
                </label>
                <Input
                  value={formData.firstName}
                  onChange={(e) =>
                    handleInputChange('firstName', e.target.value)
                  }
                  onBlur={() => handleInputBlur('firstName')}
                  placeholder="John"
                  maxLength={50}
                  className={`${errors.firstName ? 'border-red-500 dark:border-red-400' : touched.firstName && !errors.firstName ? 'border-green-500 dark:border-green-400' : 'dark:bg-gray-900 dark:border-gray-600 dark:text-white'}`}
                />
                {errors.firstName && (
                  <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                    {errors.firstName}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Last Name *
                </label>
                <Input
                  value={formData.lastName}
                  onChange={(e) =>
                    handleInputChange('lastName', e.target.value)
                  }
                  onBlur={() => handleInputBlur('lastName')}
                  placeholder="Doe"
                  maxLength={50}
                  className={`${errors.lastName ? 'border-red-500 dark:border-red-400' : touched.lastName && !errors.lastName ? 'border-green-500 dark:border-green-400' : 'dark:bg-gray-900 dark:border-gray-600 dark:text-white'}`}
                />
                {errors.lastName && (
                  <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                    {errors.lastName}
                  </p>
                )}
              </div>
            </div>
          </div>
        </Card>

        {/* Address Information */}
        <Card className="p-4 dark:bg-gray-800 dark:border-gray-700">
          <div className="flex items-center space-x-2 mb-3">
            <MapPin className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            <h3 className="font-medium text-gray-900 dark:text-white">
              Address Information
            </h3>
          </div>

          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Street Address *
              </label>
              {isLoaded && !loadError ? (
                <div className="relative">
                  <PlacesAutocompleteInput
                    value={formData.street}
                    onChange={(value) => handleInputChange('street', value)}
                    onSelect={handleAddressSelect}
                    placeholder="Start typing your address..."
                    searchOptions={searchOptions}
                    error={!!errors.street}
                    disabled={isParsingAddress}
                    className={`${errors.street ? 'border-red-500 dark:border-red-400' : touched.street && !errors.street ? 'border-green-500 dark:border-green-400' : 'dark:bg-gray-900 dark:border-gray-600 dark:text-white'}`}
                  />
                  {isParsingAddress && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                    </div>
                  )}
                </div>
              ) : (
                <Input
                  value={formData.street}
                  onChange={(e) => handleInputChange('street', e.target.value)}
                  onBlur={() => handleInputBlur('street')}
                  placeholder={
                    loadError
                      ? 'Address lookup unavailable - enter manually'
                      : 'Loading address lookup...'
                  }
                  maxLength={100}
                  className={`${errors.street ? 'border-red-500 dark:border-red-400' : touched.street && !errors.street ? 'border-green-500 dark:border-green-400' : 'dark:bg-gray-900 dark:border-gray-600 dark:text-white'}`}
                />
              )}
              {loadError && (
                <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-1 flex items-center">
                  <Info className="w-3 h-3 mr-1" />
                  Address autocomplete unavailable. Please enter address
                  manually.
                </p>
              )}
              {errors.street && (
                <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                  {errors.street}
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  City *
                </label>
                <Input
                  value={formData.city}
                  onChange={(e) => handleInputChange('city', e.target.value)}
                  onBlur={() => handleInputBlur('city')}
                  placeholder="Toronto"
                  maxLength={50}
                  className={`${errors.city ? 'border-red-500 dark:border-red-400' : touched.city && !errors.city ? 'border-green-500 dark:border-green-400' : 'dark:bg-gray-900 dark:border-gray-600 dark:text-white'}`}
                />
                {errors.city && (
                  <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                    {errors.city}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {countryConfig?.stateProvinceLabel || 'State/Province'}{' '}
                  {countryConfig?.stateProvinceRequired !== false ? '*' : ''}
                </label>
                <Input
                  value={formData.stateProvince}
                  onChange={(e) =>
                    handleInputChange('stateProvince', e.target.value)
                  }
                  onBlur={() => handleInputBlur('stateProvince')}
                  placeholder={
                    countryConfig?.stateProvinceLabel === 'Province'
                      ? 'Ontario'
                      : countryConfig?.stateProvinceLabel === 'State'
                        ? 'California'
                        : 'State/Province'
                  }
                  maxLength={50}
                  className={`${errors.stateProvince ? 'border-red-500 dark:border-red-400' : touched.stateProvince && !errors.stateProvince ? 'border-green-500 dark:border-green-400' : 'dark:bg-gray-900 dark:border-gray-600 dark:text-white'}`}
                />
                {errors.stateProvince && (
                  <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                    {errors.stateProvince}
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {countryConfig?.postalCodeLabel || 'Postal Code'} *
                </label>
                <Input
                  value={formData.postalCode}
                  onChange={(e) =>
                    handleInputChange('postalCode', e.target.value)
                  }
                  onBlur={() => handleInputBlur('postalCode')}
                  placeholder={
                    countryConfig?.postalCodeFormat || 'Enter postal code'
                  }
                  className={`${errors.postalCode ? 'border-red-500 dark:border-red-400' : touched.postalCode && !errors.postalCode ? 'border-green-500 dark:border-green-400' : 'dark:bg-gray-900 dark:border-gray-600 dark:text-white'}`}
                />
                {countryConfig && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 flex items-center">
                    <Info className="w-3 h-3 mr-1" />
                    Format: {countryConfig.postalCodeFormat}
                  </p>
                )}
                {errors.postalCode && (
                  <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                    {errors.postalCode}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Country Code *
                </label>
                <Input
                  value={formData.country}
                  onChange={(e) => handleInputChange('country', e.target.value)}
                  onBlur={() => handleInputBlur('country')}
                  placeholder="CA"
                  maxLength={2}
                  className={`${errors.country ? 'border-red-500 dark:border-red-400' : touched.country && !errors.country ? 'border-green-500 dark:border-green-400' : 'dark:bg-gray-900 dark:border-gray-600 dark:text-white'}`}
                />
                {formData.country && countryConfig && (
                  <p className="text-xs text-green-600 dark:text-green-400 mt-1 flex items-center">
                    <Flag className="w-3 h-3 mr-1" />
                    {countryConfig.name}
                  </p>
                )}
                {errors.country && (
                  <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                    {errors.country}
                  </p>
                )}
              </div>
            </div>
          </div>
        </Card>

        {/* Country Mismatch Warning - Only show when transferData exists */}
        {transferData &&
          formData.country &&
          formData.country !== transferData.receiveCountry && (
            <Card className="p-3 bg-yellow-50 border-yellow-200 dark:bg-yellow-900 dark:border-yellow-700">
              <div className="flex items-start space-x-2">
                <Info className="w-4 h-4 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm text-yellow-600 dark:text-yellow-400 font-medium">
                    Country Mismatch Warning
                  </p>
                  <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
                    You're creating a recipient for{' '}
                    {getCountryConfig(formData.country)?.name ||
                      formData.country}
                    , but your transfer is set to go to{' '}
                    {getCountryConfig(transferData.receiveCountry)?.name ||
                      transferData.receiveCountry}
                    .
                  </p>
                </div>
              </div>
            </Card>
          )}

        {/* API Status */}
        {createBeneficiaryMutation.isError && (
          <Card className="p-3 bg-red-50 border-red-200 dark:bg-red-900 dark:border-red-700">
            <p className="text-sm text-red-600 dark:text-red-400">
              Failed to create Recipient:{' '}
              {(() => {
                const error = createBeneficiaryMutation.error as Error & {
                  response?: {
                    data?: string | { message?: string; error?: string }
                  }
                }
                // Extract the actual error message from API response
                if (error?.response?.data) {
                  if (typeof error.response.data === 'string') {
                    return error.response.data
                  } else if (error.response.data.message) {
                    return error.response.data.message
                  } else if (error.response.data.error) {
                    return error.response.data.error
                  }
                }
                return (
                  error?.message || 'Please check all fields and try again.'
                )
              })()}
            </p>
          </Card>
        )}
      </div>

      {/* Create Beneficiary Button */}
      <div className="p-4 border-t border-gray-100 dark:border-gray-700 dark:bg-gray-900">
        <Button
          onClick={handleSubmit}
          disabled={!isFormValid || createBeneficiaryMutation.isPending}
          className="w-full bg-blue-600 hover:bg-gray-800 text-white h-10 text-sm font-medium rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {createBeneficiaryMutation.isPending ? (
            <div className="flex items-center space-x-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              <span>Creating Recipient...</span>
            </div>
          ) : (
            'Create Recipient'
          )}
        </Button>
      </div>

      {/* Recipient Type Selection Drawer - Constrained to widget */}
      {showRecipientTypeDialog && (
        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-end justify-center z-50">
          <div
            className={`w-full bg-white dark:bg-gray-800 rounded-t-2xl transform transition-transform duration-300 ease-out ${
              showRecipientTypeDialog ? 'translate-y-0' : 'translate-y-full'
            }`}
            style={{ maxHeight: '70%' }}
          >
            {/* Drawer Handle */}
            <div className="flex justify-center pt-3 pb-2">
              <div className="w-8 h-1 bg-gray-300 dark:bg-gray-600 rounded-full"></div>
            </div>

            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                Select Recipient Type
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Who are you sending money to?
              </p>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              <Button
                onClick={() => handleRecipientTypeSelect('other')}
                className="w-full h-16 bg-white hover:bg-gray-50 border-2 border-gray-200 hover:border-blue-300 text-gray-900 dark:bg-gray-700 dark:hover:bg-gray-600 dark:border-gray-600 dark:hover:border-blue-400 dark:text-white transition-all"
                variant="outline"
              >
                <div className="flex items-center justify-start w-full space-x-4">
                  <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center flex-shrink-0">
                    <UserCheck className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div className="text-left flex-1">
                    <h4 className="font-medium">Someone Else</h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Send to another person
                    </p>
                  </div>
                </div>
              </Button>

              <Button
                onClick={() => handleRecipientTypeSelect('self')}
                disabled
                className="w-full h-16 bg-white border-2 border-gray-200 text-gray-900 dark:bg-gray-700 dark:border-gray-600 dark:text-white transition-all opacity-60 cursor-not-allowed relative overflow-hidden"
                variant="outline"
              >
                <div className="flex items-center justify-start w-full space-x-4">
                  <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900 flex items-center justify-center flex-shrink-0">
                    <User className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div className="text-left flex-1">
                    <h4 className="font-medium">To Myself</h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Send to your own account
                    </p>
                  </div>
                </div>
                <div className="absolute inset-0 flex items-center justify-end pr-4 bg-white/40 dark:bg-gray-700/40">
                  <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 px-3 py-1 rounded-full shadow-sm">
                    Coming soon
                  </span>
                </div>
              </Button>
              <Button
                onClick={() => handleRecipientTypeSelect('business')}
                disabled
                className="w-full h-16 bg-white border-2 border-gray-200 text-gray-900 dark:bg-gray-700 dark:border-gray-600 dark:text-white transition-all opacity-60 cursor-not-allowed relative overflow-hidden"
                variant="outline"
              >
                <div className="flex items-center justify-start w-full space-x-4">
                  <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center flex-shrink-0">
                    <Building className="w-5 h-5 text-green-600 dark:text-green-400" />
                  </div>
                  <div className="text-left flex-1">
                    <h4 className="font-medium">Business</h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Company or organization
                    </p>
                  </div>
                </div>
                <div className="absolute inset-0 flex items-center justify-end pr-4 bg-white/40 dark:bg-gray-700/40">
                  <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 px-3 py-1 rounded-full shadow-sm">
                    Coming soon
                  </span>
                </div>
              </Button>

              <Button
                onClick={() => setShowRecipientTypeDialog(false)}
                variant="outline"
                className="w-full mt-4"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AddBeneficiaryScreen
